import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Select,
  Modal,
} from 'antd';
import Axios from 'axios';
import MailVariableCheckPanel from './MailVariableCheckPanel';
import MailVariablePopover from './MailVariablePopover';
import {
  analyzeMailVariables,
  replaceVariable,
  toTag,
  REVERT_NOTIFY_SYSTEM_KEY,
} from '../../Config/mailVariables';
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const MakeTemplatePage = (props) => {
  const [value, setValue] = useState('');
  const [mode, setMode] = useState('new');
  const [surveyInfo, setSurveyInfo] = useState([]);
  // 編集対象テンプレートの _id（保存時のPUTで使用）。
  // 一覧からの遷移では location.state、別タブ遷移では ?mt=<id> から解決する。
  const [editId, setEditId] = useState(null);
  // 編集対象テンプレートの system_key（回答差戻し通知などの固定テンプレート）。
  // 変数チェック・変数の挿入の対象セットを切り替えるためだけに使い、保存データ（value）には含めない。
  const [systemKey, setSystemKey] = useState(null);
  const isRevertNotify = systemKey === REVERT_NOTIFY_SYSTEM_KEY;
  const tInfo = props.location.state;
  const { user } = props;

  // 変数挿入先のフィールド（件名 or 本文）。最後にフォーカスした方へ挿入する（既定は本文）。
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const [lastFocused, setLastFocused] = useState('body');

  // 件名・本文の {$変数$} をリアルタイム解析（定義済み / 条件付き / 未定義 / 形式エラー）
  // 回答差戻し通知テンプレートは、差戻し通知で置換される変数だけを定義済みとして検証する
  const analysis = useMemo(
    () =>
      analyzeMailVariables(value && value.subject, value && value.body, {
        systemKey,
      }),
    [value, systemKey]
  );

  // フィールドの実 DOM 要素（antd Input = ref.current.input / TextArea = ref.current.resizableTextArea.textArea）
  const getFieldElement = (field) => {
    if (field === 'subject') {
      return subjectRef.current && subjectRef.current.input;
    }
    return (
      bodyRef.current &&
      bodyRef.current.resizableTextArea &&
      bodyRef.current.resizableTextArea.textArea
    );
  };

  // 変数一覧のクリックで {$key$} をカーソル位置に挿入する
  const insertVariable = (key) => {
    const field = lastFocused === 'subject' ? 'subject' : 'body';
    const el = getFieldElement(field);
    const current = (value && value[field]) || '';
    const tag = toTag(key);
    let start = current.length;
    let end = current.length;
    if (el && typeof el.selectionStart === 'number') {
      start = el.selectionStart;
      end = el.selectionEnd;
    }
    const next = current.slice(0, start) + tag + current.slice(end);
    setValue({ ...value, [field]: next });
    // 描画後にカーソルを挿入した変数の直後へ移し、フォーカスを戻す（連続挿入できるように）
    const caret = start + tag.length;
    window.requestAnimationFrame(() => {
      const target = getFieldElement(field);
      if (target && typeof target.setSelectionRange === 'function') {
        target.focus();
        target.setSelectionRange(caret, caret);
      }
    });
  };

  // 未定義変数を候補の変数へ一括置換（件名・本文の両方）
  const handleReplaceVariable = (fromKey, toKey) => {
    const s = replaceVariable((value && value.subject) || '', fromKey, toKey);
    const b = replaceVariable((value && value.body) || '', fromKey, toKey);
    const count = s.count + b.count;
    if (count === 0) return;
    setValue({ ...value, subject: s.text, body: b.text });
    message.info({
      content: `${toTag(fromKey)} を ${toTag(toKey)} に ${count} 箇所置換しました。`,
      key: 'replace-variable',
      duration: 3,
    });
  };

  // 保存前の変数ガード。
  //  - 形式エラー: どの配信でも置換されないため保存不可（修正を促す）
  //  - 未定義変数: CSVから配信の列名である可能性があるため、確認の上で保存を許可
  //  - 問題なし: そのまま保存
  const guardSave = (doSave) => {
    if (!analysis.hasIssues) {
      doSave();
      return;
    }
    const listStyle = {
      fontFamily: 'Menlo, Consolas, monospace',
      margin: 'var(--space-1) 0 var(--space-3)',
      paddingLeft: 'var(--space-5)',
    };
    const content = (
      <div>
        {analysis.malformed.length > 0 && (
          <>
            <Text strong type="danger">
              形式エラー（修正が必要です）
            </Text>
            <ul style={listStyle}>
              {analysis.malformed.map((m) => (
                <li key={m.raw}>{m.raw}</li>
              ))}
            </ul>
          </>
        )}
        {analysis.unknown.length > 0 && (
          <>
            <Text strong type="danger">
              未定義の変数
            </Text>
            <ul style={listStyle}>
              {analysis.unknown.map((u) => (
                <li key={u.key}>
                  {toTag(u.key)}
                  {u.suggestions.length > 0 && (
                    <Text type="secondary">
                      {'　→ '}
                      {u.suggestions.map((sg) => toTag(sg)).join(' / ')}{' '}
                      ではありませんか？
                    </Text>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        <Text style={{ display: 'block', lineHeight: 'var(--leading-normal)' }}>
          {analysis.isBlocked
            ? '形式エラーの変数はどの配信でも置換されません。{$変数名$} の形式に修正してから保存してください。'
            : analysis.isRevertNotify
            ? '未定義の変数は回答差戻し通知では置換されず、原文のまま受信者に届きます。'
            : '未定義の変数はサーベイお知らせ配信では置換されず、原文のまま受信者に届きます。CSVから配信専用のテンプレートで CSV の列名を指定している場合のみ、このまま保存してください。'}
        </Text>
      </div>
    );
    Modal.confirm({
      title: analysis.isBlocked
        ? '形式エラーの変数があります'
        : '未定義の変数があります',
      content,
      width: 560,
      // 安全側（戻って修正）を primary の 1 操作にし、続行は default に落とす
      okText: 'このまま保存する',
      okButtonProps: { type: 'default', disabled: analysis.isBlocked },
      cancelText: '戻って修正する',
      cancelButtonProps: { type: 'primary' },
      onOk: () => doSave(),
    });
  };

  const readDB = async () => {
    try {
      if (!user.userData) return null;
      // console.log(user.userData.isAuth);
      if (user.userData.isAuth) {
        const survey = await Axios.get('/api/survey/');
        // console.log(group.data.groups);
        // console.log(survey.data.survey);
        if (survey.data.success) {
          setSurveyInfo(survey.data.survey);
        } else {
          throw new Error('サーベイ情報取得に失敗しました。');
        }
      } else {
        throw new Error('ログイン前です。');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!user.userData) return null;
    // console.log({ user });
    readDB();

    // テンプレート情報を編集モードに反映する共通処理
    const applyEdit = (t) => {
      setMode('edit');
      setEditId(t._id);
      setSystemKey(t.system_key || null);
      setValue({
        survey_code: t.survey_code,
        survey_id: t.survey_id,
        mt_name: t.mt_name,
        mt_description: t.mt_description,
        subject: t.subject,
        from_mail: t.from_mail,
        from_name: t.from_name,
        body: t.body,
        user: user.userData._id,
      });
    };

    if (tInfo && tInfo.body) {
      // 既存: メールテンプレ一覧からの遷移（location.state で受け取る）
      applyEdit(tInfo);
    } else {
      // 別タブ遷移: URL の ?mt=<id> があれば単件取得して編集モードへ
      const mtId = new URLSearchParams(props.location.search).get('mt');
      if (mtId) {
        (async () => {
          try {
            const res = await Axios.get(`/api/mailTemplate/${mtId}`);
            if (res.data.success && res.data.mailTemplate?.body) {
              applyEdit(res.data.mailTemplate);
            } else {
              setValue({ user: user.userData._id });
            }
          } catch (error) {
            console.log(error);
            setValue({ user: user.userData._id });
          }
        })();
      } else {
        setValue({
          user: user.userData._id,
        });
      }
    }
  }, [user]);

  const onChangeTemplateName = (e) => {
    setValue({ ...value, mt_name: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeTemplateDescription = (e) => {
    setValue({ ...value, mt_description: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeTitle = (e) => {
    setValue({ ...value, subject: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeSenderMail = (e) => {
    setValue({ ...value, from_mail: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeSenderName = (e) => {
    setValue({ ...value, from_name: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeBody = (e) => {
    setValue({ ...value, body: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeSurvey = (v) => {
    setValue({
      ...value,
      survey_code: v,
      survey_id: surveyInfo.find((item) => item.survey_code === v)._id,
    });
    // console.log(surveyInfo.find((item) => item.survey_code === v)._id);
    // console.log(value);
  };
  // 実際の保存処理（変数ガードの後に呼ばれる）
  const doSubmit = async () => {
    try {
      let res = {};
      // console.log({ value });
      // console.log({ mode });
      if (mode === 'edit') {
        res = await Axios.put('/api/mailTemplate', {
          ...value,
          _id: editId,
        });
      } else {
        res = await Axios.post('/api/mailTemplate', value);
      }
      // console.log({ res });
      if (res.data.success) {
        message.success({
          content: 'テンプレート登録完了！',
          key: 'submit',
          duration: 2,
        });
        setTimeout(() => {
          props.history.push('/mailTemplate');
        }, 1000);
      } else {
        message.error({
          content: 'テンプレート登録に失敗しました。',
          key: 'submit',
          duration: 2,
        });
        throw new Error('テンプレート登録に失敗しました。');
      }
    } catch (error) {
      console.log({ error });
    }
  };
  const doSubmitCopy = async () => {
    try {
      let res = {};
      // console.log({ value });
      // console.log({ mode });
      if (mode === 'edit') {
        res = await Axios.post('/api/mailTemplate', value);
      }
      // console.log({ res });
      if (res.data.success) {
        message.success({
          content: 'テンプレート登録完了！',
          key: 'submit',
          duration: 2,
        });
        setTimeout(() => {
          props.history.push('/mailTemplate');
        }, 1000);
      } else {
        message.error({
          content: 'テンプレート登録に失敗しました。',
          key: 'submit',
          duration: 2,
        });
        throw new Error('テンプレート登録に失敗しました。');
      }
    } catch (error) {
      console.log({ error });
    }
  };

  // 保存・複製は変数チェックのガードを通す
  const onSubmit = () => guardSave(doSubmit);
  const onSubmitCopy = () => guardSave(doSubmitCopy);

  const onCancel = async () => {
    props.history.push('/mailTemplate');
  };

  return (
    <Fragment>
      <div style={{ maxWidth: '800px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>メールテンプレート作成</Title>
        </div>
        <Form onSubmit={onSubmit}>
          <div>
            <br />

            <Text strong>■ サーベイ選択</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Select
            showSearch
            style={{
              width: 600,
            }}
            placeholder="サーベイ選択"
            optionFilterProp="children"
            onChange={onChangeSurvey}
            value={value['survey_code']}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {surveyInfo.map((element) => {
              // console.log({ element });
              return (
                <Option value={element.survey_code} key={element._id}>
                  {element.survey_name}
                </Option>
              );
            })}
          </Select>
          <br />
          <br />
          <Text strong>■ テンプレート名（必須）</Text>
          <div
            style={{ display: 'flex', justifyContent: 'space-between' }}
          ></div>
          <Input
            placeholder="テンプレート名（必須）"
            name="mt_name"
            allowClear
            onChange={onChangeTemplateName}
            value={value['mt_name']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ テンプレート説明</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            placeholder="テンプレート説明"
            name="mt_description"
            allowClear
            onChange={onChangeTemplateDescription}
            value={value['mt_description']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ 件名（必須）</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            ref={subjectRef}
            placeholder="件名（必須）"
            name="subject"
            allowClear
            onChange={onChangeTitle}
            onFocus={() => setLastFocused('subject')}
            value={value['subject']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ 差出人メール（システム事前登録が必要）</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            placeholder="差出人メール"
            name="from_mail"
            allowClear
            onChange={onChangeSenderMail}
            value={value['from_mail']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ 差出人名</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            placeholder="差出人名"
            name="from_name"
            allowClear
            onChange={onChangeSenderName}
            value={value['from_name']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ メール本文（html）</Text>
            {isRevertNotify ? (
              <MailVariablePopover kind="revert" onInsert={insertVariable} />
            ) : (
              <>
                <MailVariablePopover kind="survey" onInsert={insertVariable} />
                　
                <MailVariablePopover kind="csv" onInsert={insertVariable} />
              </>
            )}
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <TextArea
            ref={bodyRef}
            showCount
            name="body"
            rows={20}
            placeholder="メール本文（html：改行は自動反映されます）"
            onChange={onChangeBody}
            onFocus={() => setLastFocused('body')}
            value={value['body']}
          />
          <MailVariableCheckPanel
            analysis={analysis}
            onReplace={handleReplaceVariable}
          />
          <br />
          <br />
          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            disabled={false}
          >
            保存
          </Button>
          　
          <Button
            size="large"
            onClick={onCancel}
            disabled={false}
          >
            キャンセル
          </Button>
          　
          <Button
            danger
            size="large"
            onClick={onSubmitCopy}
            disabled={mode !== 'edit'}
          >
            複製
          </Button>
        </Form>
      </div>
    </Fragment>
  );
};

export default MakeTemplatePage;
